import React, { useEffect, useRef, useState } from "react";
import { api, formatEUR } from "@/lib/api";
import { toast } from "sonner";
import { IceCream, X, Check, Ban } from "lucide-react";
import POSNegozio from "./POSNegozio";

// Palette colori assegnata ai formati in ordine
const PALETTE = ["#FB923C", "#F59E0B", "#D97706", "#EC4899", "#DB2777", "#BE185D"];
const TYPE_EMOJI = { cono: "🍦", coppetta: "🍨", altro: "🍧" };

export default function POSGelateria() {
  const inner = useRef(null);
  const [formats, setFormats] = useState([]);
  const [extrasCfg, setExtrasCfg] = useState([]);
  const [gusti, setGusti] = useState([]);
  const [otherProds, setOtherProds] = useState([]);
  const [builder, setBuilder] = useState(null); // { format, selected: [{id,name}], extras: [] }
  const [manageSoldOut, setManageSoldOut] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/products?mode=gelateria"), api.get("/categories?mode=gelateria"), api.get("/gelateria/config")]).then(([rp, rc, rcfg]) => {
      const products = (Array.isArray(rp.data) ? rp.data : []).filter((p) => p.visible !== false);
      const gustiCat = (rc.data || []).find((c) => (c.name || "").trim().toLowerCase() === "gusti");
      if (gustiCat) {
        setGusti((Array.isArray(products) ? products : []).filter((p) => p.category_id === gustiCat.id));
        setOtherProds((Array.isArray(products) ? products : []).filter((p) => p.category_id !== gustiCat.id));
      } else {
        setGusti([]);
        setOtherProds(products);
      }
      setFormats((Array.isArray(rcfg.data.formats) ? rcfg.data.formats : []).map((f, i) => ({ ...f, color: PALETTE[i % PALETTE.length], emoji: TYPE_EMOJI[f.type] || TYPE_EMOJI.altro })));
      setExtrasCfg(rcfg.data.extras || []);
    });
  }, []);

  const startBuilder = (fmt) => {
    // Prezzo libero dal tastierino: sovrascrive il prezzo del formato (come negli altri POS)
    const kp = inner.current?.getKeypad?.();
    if (kp?.value > 0) {
      inner.current?.clearKeypad?.();
      toast.success(`${fmt.label} a ${formatEUR(kp.value)} (prezzo libero)`);
      setBuilder({ format: { ...fmt, price: kp.value, priceOverridden: true }, selected: [], extras: [] });
      return;
    }
    setBuilder({ format: fmt, selected: [], extras: [] });
  };

  const toggleGusto = (g) => {
    if (!builder) return;
    if (g.sold_out) return toast.error(`${g.name} è esaurito`);
    const exists = builder.selected.find((x) => x.id === g.id);
    if (exists) {
      setBuilder({ ...builder, selected: (Array.isArray(builder.selected) ? builder.selected : []).filter((x) => x.id !== g.id) });
    } else {
      if (builder.selected.length >= builder.format.gusti) {
        return toast.error(`Max ${builder.format.gusti} gusti per ${builder.format.label}`);
      }
      setBuilder({ ...builder, selected: [...builder.selected, { id: g.id, name: g.name }] });
    }
  };

  const toggleExtra = (ex) => {
    if (!builder) return;
    const exists = builder.extras.find((x) => x.id === ex.id);
    if (exists) setBuilder({ ...builder, extras: (Array.isArray(builder.extras) ? builder.extras : []).filter((x) => x.id !== ex.id) });
    else setBuilder({ ...builder, extras: [...builder.extras, ex] });
  };

  const confirmBuilder = () => {
    if (!builder.selected.length) return toast.error("Seleziona almeno un gusto");
    const extrasPrice = builder.extras.reduce((s, e) => s + e.price, 0);
    const gustiNames = (Array.isArray(builder.selected) ? builder.selected : []).map((s) => s.name).join(", ");
    const extrasSuffix = builder.extras.length ? ` + ${(Array.isArray(builder.extras) ? builder.extras : []).map((e) => e.label).join(", ")}` : "";
    const line = {
      product_id: null,
      name: `${builder.format.label} (${gustiNames})${extrasSuffix}`,
      qty: 1,
      price: builder.format.price + extrasPrice,
      vat: 10,
    };
    inner.current?.setLines((prev) => [...prev, line]);
    toast.success(`+ ${builder.format.label}`);
    setBuilder(null);
  };

  const addOther = (p) => inner.current?.tapProduct(p);

  const toggleSoldOut = async (g) => {
    const nv = !g.sold_out;
    setGusti((prev) => (Array.isArray(prev) ? prev : []).map((x) => (x.id === g.id ? { ...x, sold_out: nv } : x)));
    try {
      await api.patch(`/products/${g.id}/sold-out?value=${nv}`);
      toast.success(nv ? `${g.name}: esaurito` : `${g.name}: di nuovo disponibile`);
    } catch {
      setGusti((prev) => (Array.isArray(prev) ? prev : []).map((x) => (x.id === g.id ? { ...x, sold_out: !nv } : x)));
      toast.error("Errore aggiornamento");
    }
  };
  const soldOutCount = (Array.isArray(gusti) ? gusti : []).filter((g) => g.sold_out).length;

  const productsArea = (
    <>
      {/* Formati cono/coppetta */}
      <div className="bg-white border border-velta-border rounded-md p-3 shrink-0">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-mono uppercase tracking-widest text-velta-mute">Scegli formato</div>
          <button
            data-testid="gel-manage-soldout-btn"
            onClick={() => { setBuilder(null); setManageSoldOut((v) => !v); }}
            className={`h-8 px-3 rounded-md text-xs font-medium inline-flex items-center gap-1.5 btn-tactile border ${
              manageSoldOut ? "bg-velta-ink text-white border-velta-ink" : soldOutCount > 0 ? "bg-amber-50 border-amber-400 text-amber-800" : "bg-white border-velta-border text-velta-mute hover:border-velta-ink"
            }`}
          >
            <Ban className="w-3.5 h-3.5" /> Gusti esauriti{soldOutCount > 0 ? ` (${soldOutCount})` : ""}
          </button>
        </div>
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
          {(Array.isArray(formats) ? formats : []).map((f) => (
            <button
              key={f.id}
              data-testid={`gel-format-${f.id}`}
              onClick={() => startBuilder(f)}
              className="relative bg-white border-2 rounded-md p-3 text-center btn-tactile hover:scale-105 transition"
              style={{ borderColor: builder?.format.id === f.id ? f.color : "var(--velta-border)" }}
            >
              <div className="text-2xl mb-1">{f.emoji}</div>
              <div className="font-medium text-xs leading-tight">{f.label}</div>
              <div className="text-[10px] text-velta-mute">{f.gusti} {f.gusti === 1 ? "gusto" : "gusti"}</div>
              <div className="font-mono font-bold text-sm" style={{ color: f.color }}>{formatEUR(f.price)}</div>
            </button>
          ))}
          {formats.length === 0 && <div className="col-span-full text-center text-velta-mute text-sm py-4">Nessun formato configurato — vai in Gestionale → Config. Gelateria.</div>}
        </div>
      </div>

      {manageSoldOut ? (
        <div className="flex-1 overflow-y-auto pos-scroll" data-testid="gel-soldout-panel">
          <div className="bg-amber-50 border border-amber-300 rounded-md p-3 mb-3 flex items-center justify-between gap-3">
            <div className="text-sm text-amber-900">
              <b>Gestione gusti esauriti</b> — tocca un gusto per segnarlo esaurito, tocca di nuovo per rimetterlo disponibile.
            </div>
            <button data-testid="gel-soldout-done-btn" onClick={() => setManageSoldOut(false)} className="h-9 px-4 bg-velta-ink text-white rounded-md text-sm font-medium btn-tactile shrink-0">
              Fatto
            </button>
          </div>
          <div className="grid grid-cols-3 lg:grid-cols-5 gap-2">
            {(Array.isArray(gusti) ? gusti : []).map((g) => (
              <button
                key={g.id}
                data-testid={`gel-soldout-gusto-${g.id}`}
                onClick={() => toggleSoldOut(g)}
                className={`border-2 rounded-md p-3 text-center btn-tactile transition ${
                  g.sold_out ? "bg-zinc-100 border-zinc-300 text-zinc-400" : "bg-white border-velta-border hover:border-velta-ink"
                }`}
              >
                <div className={`font-medium text-xs ${g.sold_out ? "line-through" : ""}`}>{g.name}</div>
                <div className={`text-[9px] font-mono uppercase tracking-widest mt-1 ${g.sold_out ? "text-red-400" : "text-emerald-600"}`}>
                  {g.sold_out ? "Esaurito" : "Disponibile"}
                </div>
              </button>
            ))}
            {gusti.length === 0 && <div className="col-span-full text-center text-velta-mute text-sm py-6">Nessun gusto a listino.</div>}
          </div>
        </div>
      ) : builder ? (
        <div className="flex-1 overflow-y-auto pos-scroll">
          <div className="bg-white border-2 rounded-md p-4 mb-3" style={{ borderColor: builder.format.color }}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-heading font-bold">{builder.format.label}</div>
                <div className="text-sm text-velta-mute">Seleziona <b>{builder.format.gusti}</b> gusti · {builder.selected.length}/{builder.format.gusti}</div>
              </div>
              <button data-testid="gel-builder-cancel" onClick={() => setBuilder(null)} className="text-velta-mute"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex flex-wrap gap-1 mb-3">
              {(Array.isArray(builder.selected) ? builder.selected : []).map((s, i) => (
                <span key={i} className="bg-velta-bg border border-velta-border rounded px-2 py-1 text-xs font-medium">{s.name}</span>
              ))}
            </div>
            <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Extra</div>
            <div className="flex flex-wrap gap-2">
              {(Array.isArray(extrasCfg) ? extrasCfg : []).map((ex) => {
                const active = builder.extras.find((x) => x.id === ex.id);
                return (
                  <button
                    key={ex.id}
                    data-testid={`gel-extra-${ex.id}`}
                    onClick={() => toggleExtra(ex)}
                    className={`h-8 px-3 rounded-full text-xs font-medium border ${active ? "bg-[#EC4899] text-white border-[#EC4899]" : "bg-white border-velta-border"}`}
                  >
                    {ex.label} {active && <Check className="w-3 h-3 inline ml-1" />} <span className="opacity-60">+{formatEUR(ex.price)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Gusti</div>
          <div className="grid grid-cols-3 lg:grid-cols-5 gap-2">
            {(Array.isArray(gusti) ? gusti : []).map((g) => {
              const active = builder.selected.find((x) => x.id === g.id);
              return (
                <button
                  key={g.id}
                  data-testid={`gel-gusto-${g.id}`}
                  onClick={() => toggleGusto(g)}
                  disabled={!!g.sold_out}
                  className={`border-2 rounded-md p-3 text-center btn-tactile transition ${
                    g.sold_out
                      ? "bg-zinc-100 border-zinc-200 text-zinc-400 cursor-not-allowed"
                      : active
                        ? "bg-white ring-2 ring-offset-2 ring-[#EC4899]"
                        : "bg-white hover:border-velta-ink"
                  }`}
                  style={{ borderColor: !g.sold_out && active ? builder.format.color : undefined }}
                >
                  <div className={`font-medium text-xs ${g.sold_out ? "line-through" : ""}`}>{g.name}</div>
                  {g.sold_out && <div className="text-[9px] font-mono uppercase tracking-widest text-red-400 mt-0.5">Esaurito</div>}
                </button>
              );
            })}
            {gusti.length === 0 && <div className="col-span-full text-center text-velta-mute text-sm py-6">Nessun gusto a listino — crea la categoria &quot;Gusti&quot; in modalità Gelateria e aggiungi i gusti come articoli.</div>}
          </div>

          <button
            data-testid="gel-builder-confirm"
            onClick={confirmBuilder}
            disabled={!builder.selected.length}
            className="mt-4 w-full h-14 text-white rounded-md font-medium btn-tactile disabled:opacity-40"
            style={{ background: builder.format.color }}
          >
            Aggiungi {builder.format.label} · {formatEUR(builder.format.price + builder.extras.reduce((s, e) => s + e.price, 0))}
          </button>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto pos-scroll">
          <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Altri articoli</div>
          <div className="grid grid-cols-3 lg:grid-cols-5 gap-2">
            {(Array.isArray(otherProds) ? otherProds : []).map((p) => (
              <button
                key={p.id}
                data-testid={`gel-other-${p.id}`}
                onClick={() => addOther(p)}
                className="bg-white border border-velta-border rounded-md p-3 h-20 text-left flex flex-col justify-between hover:border-velta-ink btn-tactile"
              >
                <div className="font-medium text-xs">{p.name}</div>
                <div className="font-mono font-bold text-sm">{formatEUR(p.price)}</div>
              </button>
            ))}
            {otherProds.length === 0 && <div className="col-span-full text-center text-velta-mute text-sm py-6">Nessun altro articolo. Scegli un formato sopra per creare un gelato.</div>}
          </div>
        </div>
      )}
    </>
  );

  return (
    <POSNegozio
      mode="gelateria"
      title="Velta One · Gelateria"
      testidPrefix="gel"
      innerRef={inner}
      headerExtra={<IceCream className="w-5 h-5 text-[#EC4899]" />}
      productsArea={productsArea}
    />
  );
}
